/**
 * <TutorReviewStage> — la relecture au centre (organism).
 *
 * Wajih, 04/11 : « au moment où je génère quelque chose et que j'ai besoin de le consulter, ça doit prendre la place
 * au centre, un peu comme sur Lovable quand j'ai un plan à revoir ». La colonne du champion garde sa largeur ; ce qui se
 * relit et se décide monte au centre, par-dessus l'activité de l'élève, la pellicule vidéo restant visible à côté.
 *
 *  - les propositions **telles que l'élève les verra**, une à la fois, ← et → pour passer de l'une à l'autre ;
 *  - **la nouvelle seule** (Wajih, live du 04/11) : ni l'original d'une « similaire », ni les ajustements en tête, ni
 *    la zone où l'élève dépose sa copie — la relecture reste épurée ; on change de réglages dans le champ ;
 *  - un geste par défaut, « Ajouter et montrer » (↵ : créer et montrer) ; « Ajouter à la liste » à côté (créer
 *    sans montrer) ; « Régénérer » en lien ; Échap ou la croix referment, le centre revient à l'activité ;
 *  - rien ne se perd du geste d'aujourd'hui : les trois propositions, chacune avec son état (en cours, prête, en
 *    échec), sa création (en cours, faite), son quiz de prérequis quand la salle l'offre.
 *
 * Le clavier (web) ne répond pas quand une saisie a le focus : une flèche ou « Entrée » dans le chat ne relit rien.
 * Pendant une présentation, la scène se pose sur ce que voient les élèves : elle ne s'ouvre que d'un geste du tuteur,
 * et le dit (`presenting`).
 *
 * `<TutorReviewPeek>` en est l'aperçu rapide : survoler une proposition dans la colonne la montre au centre, sans
 * quitter le fil ; la toucher ouvre la relecture.
 */
import React, { useEffect, useRef } from "react"
import { Animated, Platform, Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { ScrollView } from "../../atoms/ScrollView"
import { type UIGeneratedActivity } from "../../../ports/primary/ui/types"
import { Banner } from "../../atoms/Banner"
import { WaitingText } from "../../atoms/WaitingText"
import { Badge } from "../../atoms/Badge"
import { Button } from "../../atoms/Buttons/Button"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { SubjectBadge } from "../../atoms/SubjectBadge"
import { Switch } from "../../atoms/Switch"
import { Text } from "../../atoms/typography/Text"
import { usePresence } from "../../hooks/usePresence"
import { Kbd } from "../../molecules/TutorDraftComposer"
import { RichText } from "../../molecules/RichText"
import { shadowStyle } from "../../utils/shadow"
import { listenEscape } from "../../utils/listenEscape"
import { isEditableTarget, listenEnterKey } from "../../utils/shutterKey"
import { useFadeIn } from "../../hooks/useFadeIn"

export type TutorReviewStageProps = {
    open: boolean
    /** Pour qui : « Léa », « tout le groupe ». */
    studentName: string
    /** « 3 propositions · Fractions ». */
    heading: string
    proposals: UIGeneratedActivity[]
    index: number
    /** Absent : une seule proposition, pas de navigation. */
    onChangeIndex?: (index: number) => void
    /** La salle offre le quiz de prérequis : sa bascule, sur la proposition regardée. */
    quizAvailable?: boolean
    onToggleQuiz?: (next: boolean) => void
    /** « Ajouter et montrer » : créer l'activité et la montrer à l'élève, le geste par défaut. */
    onSend: () => void
    /** « Ajouter à la liste ». Absent : pas de bouton (une proposition du champion est faite pour être montrée). */
    onAddOnly?: () => void
    /**
     * Hors live (« Mes élèves » dans le cadre du live) : rien ne se montre à l'élève. Le geste principal (`onSend`,
     * ↵ compris) s'écrit « Ajouter à la liste » (l'appelant ne passe alors pas `onAddOnly`). Défaut `false`.
     */
    offLive?: boolean
    /** « Régénérer ». Absent : pas de lien. */
    onRegenerate?: () => void
    onClose: () => void
    /** La proposition envoyée (créée et montrée) : l'accusé prend la place des gestes, avant que la scène se referme. */
    sentId?: string | number | null
    /** Une présentation tourne : les élèves voient la scène, elle le dit. */
    presenting?: boolean
    /** Le mot du champion qui écrit une proposition encore en cours. */
    writingLabel: string
    testID?: string
}

const TEXTS = {
    eyebrow: (name: string) => `Relecture · pour ${name}`,
    close: "Fermer la relecture",
    escape: "Échap",
    previous: "Proposition précédente",
    next: "Proposition suivante",
    seen: (name: string) => `Ce que ${name} verra`,
    quiz: "Quiz de prérequis",
    regenerate: "Régénérer",
    addOnly: "Ajouter à la liste",
    addTo: (name: string) => `Ajouter à la liste de ${name}`,
    send: "Ajouter et montrer",
    sendTo: (name: string) => `Ajouter et montrer à ${name}`,
    sent: (name: string) => `Montrée à ${name}, qui la voit maintenant`,
    added: "Ajoutée à sa liste",
    failed: "Cette proposition n'a pas abouti. Les autres restent là, ou régénère.",
    presenting: "Tu présentes : tes élèves voient cette relecture.",
}

type WebKeyEvent = { key?: string; target?: unknown }
type WebDocument = {
    addEventListener: (type: string, listener: (event: WebKeyEvent) => void) => void
    removeEventListener: (type: string, listener: (event: WebKeyEvent) => void) => void
}

const NO_INDEX: (index: number) => void = () => undefined

export function TutorReviewStage(props: TutorReviewStageProps) {
    const {
        open,
        studentName,
        heading,
        proposals,
        index,
        onChangeIndex = NO_INDEX,
        quizAvailable = false,
        onToggleQuiz,
        onSend,
        onAddOnly,
        offLive = false,
        onRegenerate,
        onClose,
        sentId = null,
        presenting = false,
        writingLabel,
        testID,
    } = props
    const { progress, mounted } = usePresence(open)
    const current = proposals[Math.min(index, proposals.length - 1)]
    const count = proposals.length
    const ready = current?.status === "ready"
    const creating = current?.creation === "creating"
    const created = current?.creation === "created"
    const sent = created && sentId != null && String(sentId) === String(current?.id)
    const canAct = ready && !created && !creating

    // Le clavier du web : ← → passent d'une proposition à l'autre, ↵ envoie, Échap referme — sauf pendant une saisie.
    const keys = useRef({ index, count, onChangeIndex, onSend, onClose, canAct })
    keys.current = { index, count, onChangeIndex, onSend, onClose, canAct }
    useEffect(() => {
        if (!open || Platform.OS !== "web") return undefined
        const doc = (globalThis as { document?: WebDocument }).document
        const onKeyDown = (event: WebKeyEvent) => {
            const k = keys.current
            // Une saisie a le focus : le clavier est à elle (le chat, le brouillon), pas à la relecture.
            if (isEditableTarget(event.target) || k.count === 0) return
            if (event.key === "ArrowRight") k.onChangeIndex((k.index + 1) % k.count)
            if (event.key === "ArrowLeft") k.onChangeIndex((k.index - 1 + k.count) % k.count)
        }
        doc?.addEventListener("keydown", onKeyDown)
        // Entrée envoie ; sur un bouton atteint à la tabulation (« Ajouter à la liste », « Régénérer »), elle est à
        // lui. Après un clic (le quiz basculé, une flèche), elle envoie quand même.
        const stopEnter = listenEnterKey(() => {
            if (keys.current.count === 0 || !keys.current.canAct) return false
            keys.current.onSend()
            return true
        })
        return () => {
            doc?.removeEventListener("keydown", onKeyDown)
            stopEnter()
        }
    }, [open])
    // Échap au web, et le retour d'Android, referment la relecture.
    useEffect(() => (open ? listenEscape(() => keys.current.onClose()) : undefined), [open])

    if (!mounted || current == null) return null

    const rise = progress.interpolate({ inputRange: [0, 1], outputRange: [16, 0] })
    const grow = progress.interpolate({ inputRange: [0, 1], outputRange: [0.98, 1] })

    return (
        <View style={styles.root} testID={testID}>
            <Animated.View style={[styles.scrim, { opacity: progress }]}>
                <Pressable style={styles.fill} onPress={onClose} accessibilityLabel={TEXTS.close} />
            </Animated.View>
            <Animated.View
                style={[styles.stage, { opacity: progress, transform: [{ translateY: rise }, { scale: grow }] }]}
            >
                {/* En tête : à qui, quoi, et comment sortir. */}
                <Row align="start" justify="between" style={styles.head}>
                    <Stack gap="1">
                        <Text size="text-xs" weight="bold" color="role-strong" uppercase>
                            {TEXTS.eyebrow(studentName)}
                        </Text>
                        <Text size="text-2xl" weight="bold" color="text-strong">
                            {heading}
                        </Text>
                    </Stack>
                    <Row gap="2" align="center">
                        <Kbd label={TEXTS.escape} />
                        <IconButton
                            iconName="times"
                            appearance="transparent"
                            iconColor="text-strong"
                            size={40}
                            onPress={onClose}
                            accessibilityLabel={TEXTS.close}
                        />
                    </Row>
                </Row>

                {presenting && (
                    <View style={styles.presenting}>
                        <Banner color="warning" tinted icon="chalkboard-teacher" message={TEXTS.presenting} />
                    </View>
                )}

                {/* Les trois propositions, à choisir d'un geste. */}
                {count > 1 && (
                    <View style={styles.choices}>
                        {proposals.map((proposal, position) => (
                            <ProposalTab
                                key={proposal.id}
                                proposal={proposal}
                                position={position}
                                active={position === index}
                                onPress={() => onChangeIndex(position)}
                                writingLabel={writingLabel}
                            />
                        ))}
                    </View>
                )}

                {/* La nouvelle proposition seule, telle que l'élève la verra. */}
                <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
                    <Row gap="3" align="center" justify="center">
                        {count > 1 && (
                            <IconButton
                                iconName="chevron-left"
                                appearance="filled"
                                color="surface-raised"
                                iconColor="text-strong"
                                size={40}
                                onPress={() => onChangeIndex((index - 1 + count) % count)}
                                accessibilityLabel={TEXTS.previous}
                            />
                        )}
                        {current.status === "ready" ? (
                            <StudentSheet key={current.id} proposal={current} label={TEXTS.seen(studentName)} />
                        ) : (
                            <View style={styles.sheetColumn}>
                                <View style={styles.sheet(false)}>
                                    {current.status === "pending" ? (
                                        <WaitingText align="start">{writingLabel}</WaitingText>
                                    ) : (
                                        <Banner
                                            color="danger"
                                            tinted
                                            icon="exclamation-circle"
                                            message={TEXTS.failed}
                                        />
                                    )}
                                </View>
                            </View>
                        )}
                        {count > 1 && (
                            <IconButton
                                iconName="chevron-right"
                                appearance="filled"
                                color="surface-raised"
                                iconColor="text-strong"
                                size={40}
                                onPress={() => onChangeIndex((index + 1) % count)}
                                accessibilityLabel={TEXTS.next}
                            />
                        )}
                    </Row>
                </ScrollView>

                {/* Un seul geste principal. */}
                <Row align="center" justify="between" style={[styles.foot, styles.wrap]}>
                    <Row gap="4" align="center" style={styles.wrap}>
                        {quizAvailable && onToggleQuiz != null && ready && (
                            <Switch
                                checked={current.quizEnabled === true}
                                onChange={onToggleQuiz}
                                label={TEXTS.quiz}
                                disabled={created || creating}
                            />
                        )}
                        {onRegenerate != null && (
                            <Button
                                label={TEXTS.regenerate}
                                iconName="redo"
                                variant="ghost"
                                size="sm"
                                onPress={onRegenerate}
                            />
                        )}
                    </Row>
                    {created ? (
                        <Row gap="2" align="center" style={styles.sent}>
                            <Icon name="check-circle" size={18} color="success" />
                            <Text weight="semibold" color="text-success">
                                {sent ? TEXTS.sent(studentName) : TEXTS.added}
                            </Text>
                        </Row>
                    ) : (
                        <Row gap="3" align="center" style={styles.wrap}>
                            {onAddOnly != null && (
                                <Button
                                    label={TEXTS.addOnly}
                                    variant="outline"
                                    onPress={onAddOnly}
                                    disabled={!ready || creating}
                                />
                            )}
                            <Button
                                label={offLive ? TEXTS.addOnly : TEXTS.send}
                                accessibilityLabel={offLive ? TEXTS.addTo(studentName) : TEXTS.sendTo(studentName)}
                                iconName={offLive ? "plus" : "telegram-plane"}
                                onPress={onSend}
                                disabled={!ready}
                                loading={creating}
                            />
                            <Kbd label="↵" />
                        </Row>
                    )}
                </Row>
            </Animated.View>
        </View>
    )
}

/** Un onglet de la fournée : sa place, son type, son titre. */
function ProposalTab(props: {
    proposal: UIGeneratedActivity
    position: number
    active: boolean
    onPress: () => void
    writingLabel: string
}) {
    const { proposal, position, active, onPress, writingLabel } = props
    return (
        <Pressable onPress={onPress} style={styles.choice(active)} accessibilityRole="tab" aria-selected={active}>
            <Row gap="2" align="center">
                <View style={styles.choiceNumber(active)}>
                    <Text size="text-xs" weight="bold" color={active ? "white" : "text-muted"}>
                        {String(position + 1)}
                    </Text>
                </View>
                <Text size="text-xs" weight="bold" color={active ? "role-strong" : "text-muted"} uppercase>
                    {proposal.variationLabel ?? ""}
                </Text>
            </Row>
            {proposal.status === "ready" ? (
                <Text size="text-sm" weight="semibold" color="text-strong" numberOfLines={2}>
                    {proposal.title ?? ""}
                </Text>
            ) : proposal.status === "pending" ? (
                <WaitingText size="text-sm" align="start" announce={false}>
                    {writingLabel}
                </WaitingText>
            ) : (
                <Text size="text-sm" color="text-muted" numberOfLines={2}>
                    {TEXTS.failed}
                </Text>
            )}
        </Pressable>
    )
}

/** La feuille de l'élève : ce qu'il lira, comme il le lira. */
function StudentSheet(props: { proposal: UIGeneratedActivity; label: string; compact?: boolean }) {
    const { proposal, label, compact = false } = props
    // L'arrivée d'une proposition : un fondu court, pour sentir qu'on a changé de feuille.
    const appear = useFadeIn(true)

    return (
        <Animated.View style={[styles.sheetColumn, { opacity: appear }]}>
            <Row gap="2" align="center" style={styles.sheetLabel}>
                <Icon name="eye" size={12} color="role" />
                <Text size="text-xs" weight="bold" color="role-strong" uppercase>
                    {label}
                </Text>
            </Row>
            <View style={styles.sheet(compact)}>
                <Stack gap="3">
                    <Row gap="2" align="center">
                        {proposal.subject != null && <SubjectBadge subject={proposal.subject} />}
                        {proposal.chapterName != null && (
                            <Text size="text-sm" color="text-muted">
                                {proposal.chapterName}
                            </Text>
                        )}
                        {proposal.levelLabel != null && <Badge text={proposal.levelLabel} variant="info" tinted />}
                    </Row>
                    <Text size={compact ? "text-lg" : "text-xl"} weight="bold" color="text-strong">
                        {proposal.title ?? ""}
                    </Text>
                    {proposal.content != null && <RichText value={proposal.content} />}
                </Stack>
            </View>
        </Animated.View>
    )
}

export type TutorReviewPeekProps = {
    proposal: UIGeneratedActivity
    studentName: string
    testID?: string
}

/** L'aperçu rapide : la proposition survolée dans la colonne, posée au centre, sans geste. */
export function TutorReviewPeek(props: TutorReviewPeekProps) {
    const { proposal, studentName, testID } = props
    return (
        <View style={styles.peekRoot} pointerEvents="none" testID={testID}>
            <View style={styles.peek}>
                <StudentSheet proposal={proposal} label={TEXTS.seen(studentName)} compact />
            </View>
        </View>
    )
}

const SHEET_MAX = 560

const styles = StyleSheet.create((theme) => ({
    root: { position: "absolute" as const, top: 0, left: 0, right: 0, bottom: 0, zIndex: 5 },
    fill: { flex: 1 },
    scrim: {
        position: "absolute" as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: theme.colors.scrim,
    },
    stage: {
        flex: 1,
        margin: theme.spacing["3"],
        borderRadius: theme.radius.xl,
        backgroundColor: theme.colors["surface-raised"],
        overflow: "hidden" as const,
        ...shadowStyle(theme, "floating"),
    },
    head: {
        paddingHorizontal: theme.spacing["6"],
        paddingTop: theme.spacing["5"],
        paddingBottom: theme.spacing["3"],
    },
    choices: {
        flexDirection: "row" as const,
        gap: theme.spacing["3"],
        paddingHorizontal: theme.spacing["6"],
        paddingBottom: theme.spacing["4"],
    },
    choice: (active: boolean) => ({
        flex: 1,
        minWidth: 0,
        gap: theme.spacing["1"],
        padding: theme.spacing["3"],
        borderRadius: theme.radius.md,
        borderWidth: 2,
        borderColor: active ? theme.colors.role : theme.colors["border-subtle"],
        backgroundColor: active ? theme.colors.surface : theme.colors["surface-subtle"],
    }),
    choiceNumber: (active: boolean) => ({
        width: 20,
        height: 20,
        borderRadius: 10,
        alignItems: "center" as const,
        justifyContent: "center" as const,
        backgroundColor: active ? theme.colors.role : theme.colors.border,
    }),
    /** La feuille de l'élève (et l'original) défilent quand ils dépassent : jamais par-dessus la tête de la scène. */
    body: {
        flex: 1,
        minHeight: 0,
        backgroundColor: theme.colors["surface-subtle"],
        borderTopWidth: 1,
        borderBottomWidth: 1,
        borderColor: theme.colors["border-subtle"],
    },
    bodyContent: {
        flexGrow: 1,
        justifyContent: "center" as const,
        paddingHorizontal: theme.spacing["4"],
        paddingVertical: theme.spacing["4"],
    },
    sheetColumn: { flex: 1, maxWidth: SHEET_MAX, minWidth: 0, gap: theme.spacing["2"] },
    sheetLabel: { flexWrap: "wrap" as const },
    sheet: (compact: boolean) => ({
        padding: theme.spacing[compact ? "4" : "6"],
        borderRadius: theme.radius.lg,
        backgroundColor: theme.colors["surface-raised"],
        ...shadowStyle(theme, "card"),
        // L'aperçu rapide est borné : la feuille s'y coupe au lieu de déborder de son cadre.
        ...(compact ? { flexShrink: 1, minHeight: 0, overflow: "hidden" as const } : null),
    }),
    foot: { paddingHorizontal: theme.spacing["6"], paddingVertical: theme.spacing["4"], gap: theme.spacing["3"] },
    wrap: { flexWrap: "wrap" as const },
    presenting: { paddingHorizontal: theme.spacing["6"], paddingBottom: theme.spacing["3"] },
    sent: {
        paddingHorizontal: theme.spacing["4"],
        paddingVertical: theme.spacing["2"],
        borderRadius: theme.radius.full,
        backgroundColor: theme.colors.successLight,
    },
    peekRoot: {
        position: "absolute" as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        alignItems: "center" as const,
        justifyContent: "center" as const,
        zIndex: 5,
    },
    /**
     * Au plus 60 % de la hauteur du centre (Wajih, 05/11 : un énoncé long le faisait monter sur tout l'écran). Ce qui
     * dépasse se coupe : l'aperçu ne prend jamais la souris (il volerait le survol qui l'ouvre), la relecture complète
     * est au toucher de la proposition.
     */
    peek: {
        width: "86%",
        maxWidth: 560,
        maxHeight: "60%",
        overflow: "hidden" as const,
        gap: theme.spacing["3"],
        padding: theme.spacing["4"],
        borderRadius: theme.radius.xl,
        backgroundColor: theme.colors["surface-subtle"],
        borderWidth: 1,
        borderColor: theme.colors["border-subtle"],
        ...shadowStyle(theme, "floating"),
    },
}))
